import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MapPin, PhoneIncoming, PhoneOutgoing, Clock, Building2 } from 'lucide-react';
import { API, TEAL } from './domainUtils';

const fmtDur = (secs) => {
  const s = Math.max(0, Number(secs) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
};

const fmtRange = (from, to) => {
  if (!from || !to) return '—';
  const df = new Date(from);
  const dt = new Date(to);
  if (isNaN(df) || isNaN(dt)) return '—';
  const opts = { month: 'short', day: 'numeric' };
  const a = df.toLocaleDateString(undefined, opts);
  const b = dt.toLocaleDateString(undefined, opts);
  return a === b ? a : `${a} → ${b}`;
};

/**
 * Site Performance panel — grouped view of a domain's calls by site label
 * (from `domain.extension_sites`). Reveals per-site call volume, inbound
 * vs outbound split, average talk time, and the call-date range so
 * managers can spot which locations are hot and which are quiet.
 *
 * Extensions not mapped to any site fall into the "Unassigned" bucket —
 * shown last with a subtle border color hint so ops know to configure it
 * on the Overview tab.
 */
export const SitePerformancePanel = ({ domainId, token, days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/analytics/site-performance?domain_id=${domainId}&days=${days}`, { headers })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => toast.error('Failed to load site performance'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [domainId, headers, days]);

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex justify-center py-10" data-testid="site-performance-loading">
        <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
      </div>
    );
  }

  const sites = data?.sites || [];
  if (sites.length === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="site-performance-empty">
        <p className="text-white font-medium text-sm mb-2 inline-flex items-center gap-2">
          <Building2 className="w-4 h-4 text-cyan-400" /> Site Performance
        </p>
        <p className="text-white/40 text-xs">No calls in the last {days} days.</p>
      </div>
    );
  }

  const configured = data?.sites_configured || 0;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="site-performance-panel">
      <div className="flex items-baseline justify-between mb-4">
        <p className="text-white font-medium text-sm inline-flex items-center gap-2">
          <Building2 className="w-4 h-4 text-cyan-400" /> Site Performance
          <span className="text-white/30 text-xs font-normal">(last {days} days)</span>
        </p>
        {configured === 0 && (
          <span className="text-[11px] text-amber-300/80 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> No sites mapped — set them on the Overview tab
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {sites.map((s) => {
          const isUnassigned = s.site === 'Unassigned';
          return (
            <div
              key={s.site}
              className={`rounded-lg p-4 border ${
                isUnassigned
                  ? 'bg-white/3 border-dashed border-white/15'
                  : 'bg-white/5 border-cyan-500/20'
              }`}
              data-testid={`site-card-${s.site.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <MapPin className={`w-4 h-4 ${isUnassigned ? 'text-white/30' : 'text-cyan-400'}`} />
                <p className={`text-sm font-semibold ${isUnassigned ? 'text-white/50' : 'text-white'}`}>
                  {s.site}
                </p>
                <span className="ml-auto text-[10px] text-white/30 tabular-nums">
                  {s.extensions.length} ext
                </span>
              </div>

              <div className="flex items-baseline gap-2 mb-3">
                <p className="text-2xl font-bold text-white tabular-nums">{s.total_calls.toLocaleString()}</p>
                <p className="text-xs text-white/40">total calls</p>
              </div>

              <div className="text-[11px] text-white/50 mb-3 inline-flex items-center gap-1">
                {fmtRange(s.date_range?.from, s.date_range?.to)}
              </div>

              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/10">
                <div>
                  <div className="flex items-center gap-1 text-[10px] text-white/40 uppercase tracking-wider mb-0.5">
                    <PhoneIncoming className="w-3 h-3 text-emerald-400" /> In
                  </div>
                  <p className="text-sm font-semibold text-emerald-300 tabular-nums">
                    {s.inbound_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[10px] text-white/40 uppercase tracking-wider mb-0.5">
                    <PhoneOutgoing className="w-3 h-3 text-sky-400" /> Out
                  </div>
                  <p className="text-sm font-semibold text-sky-300 tabular-nums">
                    {s.outbound_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[10px] text-white/40 uppercase tracking-wider mb-0.5">
                    <Clock className="w-3 h-3 text-amber-400" /> Avg
                  </div>
                  <p className="text-sm font-semibold text-amber-300 tabular-nums">
                    {fmtDur(s.avg_talk_time_seconds)}
                  </p>
                </div>
              </div>

              {s.extensions.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-1">
                  {s.extensions.slice(0, 12).map((e) => (
                    <span
                      key={e}
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/50 border border-white/10"
                    >
                      {e}
                    </span>
                  ))}
                  {s.extensions.length > 12 && (
                    <span className="text-[10px] text-white/30 px-1">
                      +{s.extensions.length - 12} more
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Colour-coded legend uses the same teal accent as the parent tab
          so it feels part of the same design system rather than bolted-on. */}
      <div className="mt-4 flex items-center gap-4 text-[11px] text-white/30">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: TEAL }} /> Configured site
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full border border-dashed border-white/30" /> Unassigned extensions
        </span>
      </div>
    </div>
  );
};
